/**
 * useAllAppMessages Hook
 *
 * React Query hook for fetching and managing application messages across ALL applications.
 * Uses GSI5-based cross-application endpoints for efficient querying.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { appMessageService } from '../services/app-message-service';
import type {
  AppMessageSanitized,
  AppMessageListQuery,
  AppMessageView,
  MessagePriority,
} from '../../../types/app-message.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const ALL_APP_MESSAGES_QUERY_KEY = 'allAppMessages';
export const ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY = 'allAppMessagesUnreadCount';

// ============================================================================
// HOOK OPTIONS
// ============================================================================

export interface UseAllAppMessagesOptions {
  /** Show only unread messages */
  unreadOnly?: boolean;
  /** Enable/disable the query */
  enabled?: boolean;
  /** Filter by message type */
  type?: string;
  /** Pagination limit */
  limit?: number;
  /** View type (inbox, archived, deleted) - defaults to inbox */
  view?: AppMessageView;
  /** Filter by priority */
  priority?: MessagePriority;
  /** Filter by date range start */
  dateStart?: string;
  /** Filter by date range end */
  dateEnd?: string;
}

// ============================================================================
// HOOK RESULT
// ============================================================================

export interface UseAllAppMessagesResult {
  /** List of app messages from all applications */
  messages: AppMessageSanitized[];
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Total unread count across all applications */
  unreadCount: number;
  /** Unread count loading state */
  isLoadingUnreadCount: boolean;
  /** Has more pages */
  hasMore: boolean;
  /** Pagination cursor */
  cursor?: string;
  /** Refresh messages */
  refresh: () => void;
  /** Mark message as read (requires applicationId from message) */
  markAsRead: (messageId: string, applicationId: string) => Promise<void>;
  /** Archive a message (requires applicationId from message) */
  archiveMessage: (messageId: string, applicationId: string) => Promise<void>;
  /** Unarchive a message (requires applicationId from message) */
  unarchiveMessage: (messageId: string, applicationId: string) => Promise<void>;
  /** Soft delete a message (requires applicationId from message) */
  softDeleteMessage: (messageId: string, applicationId: string) => Promise<void>;
  /** Restore a deleted message (requires applicationId from message) */
  restoreMessage: (messageId: string, applicationId: string) => Promise<void>;
  /** Permanently delete a message (requires applicationId from message) */
  permanentDeleteMessage: (messageId: string, applicationId: string) => Promise<void>;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for fetching and managing app messages across all applications
 */
export function useAllAppMessages(options?: UseAllAppMessagesOptions): UseAllAppMessagesResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const tenantId = user?.tenantId || '';
  const userId = user?.userId || '';
  const view = options?.view || 'inbox';

  const enabled = options?.enabled !== false && !!tenantId && !!userId;

  // Build query key that includes all filter options
  const queryKeyParams = [
    ALL_APP_MESSAGES_QUERY_KEY,
    tenantId,
    userId,
    view,
    options?.unreadOnly,
    options?.type,
    options?.priority,
    options?.dateStart,
    options?.dateEnd,
    options?.limit,
  ];

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Fetch messages query (cross-application)
   */
  const {
    data: messagesData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<{ messages: AppMessageSanitized[]; cursor?: string }, Error>({
    queryKey: queryKeyParams,
    queryFn: async () => {
      const query: AppMessageListQuery = {
        view,
        unreadOnly: options?.unreadOnly,
        type: options?.type,
        priority: options?.priority,
        dateStart: options?.dateStart,
        dateEnd: options?.dateEnd,
        limit: options?.limit || 20,
      };
      return await appMessageService.listAllMessages(tenantId, userId, query);
    },
    enabled,
    staleTime: 15000, // 15 seconds
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return 60000;
    },
  });

  /**
   * Fetch total unread count query (cross-application)
   */
  const { data: unreadCount = 0, isLoading: isLoadingUnreadCount } = useQuery<number, Error>({
    queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
    queryFn: () => appMessageService.getTotalUnreadCount(tenantId, userId),
    enabled,
    staleTime: 15000, // 15 seconds
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return 60000;
    },
  });

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  /**
   * Mark as read mutation
   * Note: Requires applicationId because the write operation is still app-scoped
   */
  const markAsReadMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.markAsRead(tenantId, applicationId, userId, messageId),
    onSuccess: (updatedMessage) => {
      // Update the messages cache
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((msg) =>
              msg.messageId === updatedMessage.messageId ? updatedMessage : msg
            ),
          };
        }
      );
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
      });
    },
  });

  /**
   * Archive message mutation
   */
  const archiveMessageMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.archiveMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (archivedMessage) => {
      // Remove from messages cache (it moves to archived view)
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== archivedMessage.messageId),
          };
        }
      );

      // Invalidate all queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_QUERY_KEY, tenantId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (!archivedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
        });
      }
    },
  });

  /**
   * Unarchive message mutation
   */
  const unarchiveMessageMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.unarchiveMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, { messageId }) => {
      // Remove from current view (archived) as it moves back to inbox
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_QUERY_KEY, tenantId, userId],
        exact: false,
      });
    },
  });

  /**
   * Soft delete message mutation
   */
  const softDeleteMessageMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.softDeleteMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (deletedMessage, { messageId }) => {
      // Remove from current view as it moves to deleted view
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_QUERY_KEY, tenantId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (!deletedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
        });
      }
    },
  });

  /**
   * Restore message mutation
   */
  const restoreMessageMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.restoreMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, { messageId }) => {
      // Remove from current view (deleted) as it moves back to inbox
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_QUERY_KEY, tenantId, userId],
        exact: false,
      });
    },
  });

  /**
   * Permanent delete message mutation
   */
  const permanentDeleteMessageMutation = useMutation({
    mutationFn: ({ messageId, applicationId }: { messageId: string; applicationId: string }) =>
      appMessageService.permanentDeleteMessage(tenantId, applicationId, userId, messageId),
    onSuccess: (_, { messageId }) => {
      // Remove from current view permanently
      queryClient.setQueryData<{ messages: AppMessageSanitized[]; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.filter((msg) => msg.messageId !== messageId),
          };
        }
      );
    },
  });

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    messages: messagesData?.messages || [],
    isLoading,
    error: queryError ? getErrorMessage(queryError) : null,
    unreadCount,
    isLoadingUnreadCount,
    hasMore: !!messagesData?.cursor,
    cursor: messagesData?.cursor,
    refresh: () => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
      });
    },
    markAsRead: async (messageId: string, applicationId: string) => {
      await markAsReadMutation.mutateAsync({ messageId, applicationId });
    },
    archiveMessage: async (messageId: string, applicationId: string) => {
      await archiveMessageMutation.mutateAsync({ messageId, applicationId });
    },
    unarchiveMessage: async (messageId: string, applicationId: string) => {
      await unarchiveMessageMutation.mutateAsync({ messageId, applicationId });
    },
    softDeleteMessage: async (messageId: string, applicationId: string) => {
      await softDeleteMessageMutation.mutateAsync({ messageId, applicationId });
    },
    restoreMessage: async (messageId: string, applicationId: string) => {
      await restoreMessageMutation.mutateAsync({ messageId, applicationId });
    },
    permanentDeleteMessage: async (messageId: string, applicationId: string) => {
      await permanentDeleteMessageMutation.mutateAsync({ messageId, applicationId });
    },
  };
}
